import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useMemo } from 'react';
import { useTour } from '@/contexts/TourContext';
import { X } from 'lucide-react';
import { FakeCursor } from './FakeCursor';

interface TourCardProps {
  highlightRect: { top: number; left: number; width: number; height: number } | null;
  anchorFound: boolean;
  overridePlacement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

export function TourCard({ highlightRect, anchorFound, overridePlacement }: TourCardProps) {
  useLanguage();
  const { currentStep, totalSteps, steps, nextStep, prevStep, stopTour, actionFired, fireAction, stepRunId, transitioning } = useTour();
  const step = steps[currentStep];

  const placement = overridePlacement || step.placement;

  const useFixedPos = actionFired && step.postActionFixedPosition !== undefined
    ? step.postActionFixedPosition
    : step.fixedPosition;

  const isCentered = !useFixedPos && (placement === 'center' || !highlightRect);

  const cardStyle = useMemo(() => {
    const mobile = window.innerWidth < 640;
    const w = mobile ? window.innerWidth * 0.92 : 360;

    if (useFixedPos) {
      return { position: 'fixed' as const, top: useFixedPos.top, left: useFixedPos.left, width: w };
    }

    if (placement === 'center' || !highlightRect) {
      return { width: w };
    }

    const gap = 16;
    let top = 0;
    let left = 0;

    switch (placement) {
      case 'bottom':
        top = highlightRect.top + highlightRect.height + gap;
        left = highlightRect.left + highlightRect.width / 2 - w / 2;
        break;
      case 'top':
        top = highlightRect.top - gap - 200;
        left = highlightRect.left + highlightRect.width / 2 - w / 2;
        break;
      case 'left':
        top = highlightRect.top;
        left = highlightRect.left - w - gap;
        break;
      case 'right':
        top = highlightRect.top;
        left = highlightRect.left + highlightRect.width + gap;
        break;
      case 'bottom-left':
        top = highlightRect.top + highlightRect.height + gap;
        left = highlightRect.left;
        break;
    }

    const maxLeft = window.innerWidth - w - 16;
    left = Math.max(16, Math.min(left, maxLeft));
    top = Math.max(16, Math.min(top, window.innerHeight - 260));

    return { position: 'fixed' as const, top, left, width: w };
  }, [highlightRect, placement, currentStep, useFixedPos]);

  const isFinalStep = currentStep === totalSteps - 1;
  const isAutoClickStep = step.autoClickTarget && !actionFired;

  const handlePrimaryClick = () => {
    if (transitioning) return;
    if (step.primaryAction && !actionFired) {
      step.primaryAction();
      fireAction();
      return;
    }
    nextStep();
  };

  const handlePrevClick = () => {
    if (transitioning) return;
    prevStep();
  };

  const getPrimaryLabel = () => {
    if (actionFired) return isFinalStep ? 'Fechar' : translateInterfaceText("Próximo");
    if (isAutoClickStep) return translateInterfaceText("Próximo");
    return isFinalStep ? 'Fechar' : step.primaryLabel;
  };

  const cardContent = (
    <div
      className="rounded-[16px] border border-[rgba(255,255,255,0.08)] shadow-[0_16px_48px_rgba(0,0,0,0.4)] overflow-hidden relative"
      style={{
        background: 'linear-gradient(145deg, rgba(20,20,28,0.97), rgba(12,12,18,0.98))',
        backdropFilter: 'blur(20px)',
      }}
    >
      <button
        onClick={stopTour}
        className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors z-10"
        aria-label={translateInterfaceText("Fechar tour")}
      >
        <X className="h-4 w-4" />
      </button>

      <div className="p-6 pb-5">
        <h3 className="text-[16px] font-semibold text-white pr-8 mb-2">
          {step.title}
        </h3>

        <p className="text-[13.5px] text-white/60 leading-relaxed mb-5">
          {!anchorFound && step.selector
            ? step.fallbackMessage || step.description
            : step.description}
        </p>

        {/* Progress dots */}
        <div className="flex items-center gap-1.5 mb-5">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-[6px] rounded-full transition-all duration-300 ${
                i === currentStep
                  ? 'w-5 bg-[#4B7BFF]'
                  : i < currentStep
                  ? 'w-[6px] bg-[#4B7BFF]/50'
                  : 'w-[6px] bg-white/15'
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          {currentStep > 0 ? (
            <button
              onClick={handlePrevClick}
              disabled={transitioning}
              className="h-[38px] px-5 rounded-[10px] text-[13px] font-medium text-white/60 border border-white/10 hover:bg-white/5 hover:text-white/80 transition-colors disabled:opacity-50"
            >
              {translateInterfaceText("Anterior")}</button>
          ) : (
            <div />
          )}

          <button
            onClick={handlePrimaryClick}
            disabled={transitioning}
            className="h-[38px] px-6 rounded-[10px] text-[13px] font-semibold bg-[#4B7BFF] text-white hover:bg-[#4B7BFF]/90 transition-colors shadow-[0_2px_8px_rgba(75,123,255,0.35)] disabled:opacity-50"
          >
            {getPrimaryLabel()}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {step.cursorTarget && anchorFound && !actionFired && (
        <FakeCursor
          targetSelector={step.cursorTarget}
          action={step.cursorAction || 'click'}
          delay={step.cursorDelay || 500}
          onComplete={step.autoClickTarget ? () => fireAction() : step.onCursorComplete}
          stepRunId={stepRunId}
        />
      )}

      {isCentered ? (
        // Wrapper flexbox — evita conflito de transform da animação com posicionamento
        <div
          className="fixed inset-0 flex items-center justify-center pointer-events-none"
          style={{ zIndex: actionFired ? 10002 : 9995 }}
        >
          <div
            className="pointer-events-auto animate-in fade-in-0 zoom-in-95 duration-200"
            style={cardStyle}
          >
            {cardContent}
          </div>
        </div>
      ) : (
        <div
          className="pointer-events-auto animate-in fade-in-0 zoom-in-95 duration-200"
          style={{ ...cardStyle, zIndex: actionFired ? 10002 : 9995 }}
        >
          {cardContent}
        </div>
      )}
    </>
  );
}
